import type { SettingsPhase } from './types';

/**
 * Runtime override for the rollout phase, set once at bootstrap from the
 * deployed environment's runtime config.json (see apps/ais main.tsx). One
 * build artifact is deployed to every environment, so per-environment phase
 * control can't come from a Vite build-time env var — it must be set after
 * the app boots, before any consumer of `settingsPhase()` runs.
 */
let runtimePhaseOverride: SettingsPhase | undefined;

/**
 * Called once from app bootstrap (after the runtime config.json fetch) to
 * apply the deployed environment's phase. Falls back to leaving the override
 * unset (and thus to VITE_USER_SETTINGS_PHASE/default) if given anything
 * other than a valid phase.
 */
export const setRuntimeSettingsPhase = (phase: number | undefined): void => {
  runtimePhaseOverride = phase === 2 || phase === 3 ? phase : undefined;
};

/**
 * Single global rollout phase for the whole user-settings migration. One
 * control point for every domain — we migrate all owned domains together,
 * not per-domain.
 *
 * Resolution order: runtime override (deployed environments, see
 * `setRuntimeSettingsPhase`) → `VITE_USER_SETTINGS_PHASE` (local dev via
 * .env/.env.local, since local builds never go through config.json) → 1.
 * On-prem deployments intentionally have no explicit USER_SETTINGS_PHASE yet
 * and so fall through to this same default; they'll get per-system overrides
 * once that rollout is scheduled.
 *
 * - Phase 1: legacy blob only (no behaviour change; committed default).
 * - Phase 2: dual-write (blob authoritative + service kept in sync); reads
 *   prefer the service once the login-time import has run (`imported`).
 * - Phase 3: service is the sole source of truth. DEFERRED — unsafe globally
 *   until every backend/olserver blob-reader (Bucket A) retires.
 */
export const settingsPhase = (): SettingsPhase => {
  if (runtimePhaseOverride) return runtimePhaseOverride;

  const raw = import.meta.env.VITE_USER_SETTINGS_PHASE;
  const n = raw ? Number.parseInt(raw, 10) : 1;
  // Phases are only 1/2/3. Clamp anything out of range (or malformed) to the
  // safe default so a stray value like 99 can't accidentally force phase-3
  // (service-only) behaviour.
  if (n === 2) return 2;
  if (n === 3) return 3;
  return 1;
};
